import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Inbox, CreditCard, Mail, MailCheck, RefreshCw, Reply, CheckCheck, Users, Eye, LayoutTemplate } from "lucide-react";
import AdminClients from "@/components/AdminClients";
import AdminPages from "@/components/AdminPages";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";
import ReplyDrawer from "@/components/ReplyDrawer";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function StatCard({ label, value, testid }) {
  return (
    <div className="glass rounded-2xl p-6" data-testid={testid}>
      <p className="eyebrow">{label}</p>
      <p className="f-display text-3xl mt-3">{value}</p>
    </div>
  );
}

function InquiryCard({ q, onReply }) {
  const replied = (q.replies || []).length > 0;
  const fresh = !q.read;
  return (
    <article className={`glass rounded-2xl p-6 grid md:grid-cols-[1fr_1.4fr] gap-5 ${fresh ? "border-[#C48A9F]" : ""}`} data-testid="admin-inquiry-card">
      <div>
        <div className="flex items-center gap-2 text-[11px] f-mono text-[#8C7562]">
          {q.emailed ? <MailCheck size={13} className="text-[#C48A9F]" /> : <Mail size={13} />} {fmtDate(q.created_at)}
        </div>
        <h3 className="f-display text-2xl mt-2 flex items-center gap-2">{q.name}{fresh && <span className="text-[9px] uppercase tracking-[0.2em] bg-[#C48A9F] text-[#FFFDF7] rounded-full px-2 py-0.5" data-testid="admin-new-badge">New</span>}</h3>
        {q.business && <p className="text-sm text-[#57473B]">{q.business}</p>}
        {q.email && <a href={`mailto:${q.email}`} className="text-sm text-[#A8785A] hover:underline break-all" data-testid="admin-inquiry-email">{q.email}</a>}
        {q.handles && <p className="text-xs text-[#8C7562] mt-2">{q.handles}</p>}
      </div>
      <div className="text-sm leading-relaxed">
        <div className="flex flex-wrap gap-2 mb-3">
          {q.tier && <span className="text-[11px] font-semibold border border-[#E8DCCB] bg-[#FFF3F8] rounded-full px-3 py-1">{q.tier}</span>}
          {q.budget && <span className="text-[11px] font-semibold border border-[#E8DCCB] bg-[#FFFDF7] rounded-full px-3 py-1">Budget {q.budget}</span>}
        </div>
        <p className="text-[#57473B] whitespace-pre-wrap">{q.goals || <span className="text-[#B9A896]">No goals written.</span>}</p>
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <button onClick={() => onReply(q)} disabled={!q.email} className="btn-primary !py-2 !px-4" data-testid="admin-reply-btn">
            <Reply size={13} /> {replied ? "Reply again" : "Reply"}
          </button>
          {replied && (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-[#A8785A]" data-testid="admin-replied-badge">
              <CheckCheck size={13} /> Replied {fmtDate(q.replies[q.replies.length - 1].sent_at)}
            </span>
          )}
          {!q.email && <span className="text-[11px] text-[#B9A896]">No email left — can't reply</span>}
        </div>
      </div>
    </article>
  );
}

function PurchaseRow({ p }) {
  const tone = { paid: "text-[#C48A9F]", pending: "text-[#B9A896]", refunded: "text-[#A8785A]" }[p.payment_status] || "text-[#8C7562]";
  return (
    <div className="grid sm:grid-cols-[1.2fr_1fr_0.7fr_0.8fr_1.2fr] gap-3 items-center py-4 border-b border-[#E8DCCB] text-sm" data-testid="admin-purchase-row">
      <div>
        <p className="font-semibold">@{p.username || p.user_id?.slice(0, 8)}</p>
        <p className="text-[11px] text-[#A8785A]">{p.plan || "The Organizer"}{p.billing === "monthly" ? " · monthly" : ""}</p>
        {p.customer_email && <p className="text-xs text-[#8C7562] break-all">{p.customer_email}</p>}
      </div>
      <p className="text-xs text-[#8C7562]">{fmtDate(p.created_at)}</p>
      <p className="f-display text-lg">${Number(p.amount || 0).toFixed(2)}</p>
      <p className={`f-mono text-[11px] ${tone}`}>{p.payment_status}</p>
      <p className="text-[11px] text-[#B9A896] truncate">{p.receipt_sent ? "Receipt emailed" : p.payment_status === "paid" ? "No receipt (no email on file)" : ""}</p>
    </div>
  );
}

export default function AdminPage() {
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ paid_count: 0, revenue: 0, pending_count: 0 });
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  const markReplied = (id, reply) => setInquiries((list) => list.map((q) => (q.id === id ? { ...q, replies: [...(q.replies || []), reply] } : q)));

  const load = async () => {
    setBusy(true);
    try {
      const [a, b, c] = await Promise.all([api.get("/admin/inquiries"), api.get("/admin/purchases"), api.get("/admin/clients")]);
      setInquiries(a.data.inquiries);
      setPurchases(b.data.purchases);
      setStats(b.data.stats);
      setClients(c.data.clients);
      if (a.data.inquiries.some((q) => !q.read)) {
        await api.post("/admin/inquiries/mark-read");
        window.dispatchEvent(new Event("inquiries-read"));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (user?.admin) load(); }, [user]);

  if (!ready) return <div className="min-h-screen bg-[#FAF6EF]" />;
  if (!user?.admin) {
    return (
      <main className="min-h-screen vector-grid flex items-center justify-center px-6 pt-16" data-testid="admin-denied">
        <div className="glass rounded-2xl max-w-md w-full p-10 text-center">
          <span className="eyebrow">Private</span>
          <h1 className="f-display text-3xl mt-3 mb-3">Owner access only</h1>
          <p className="text-sm text-[#8C7562] mb-8">Log in with the owner account to see inquiries and purchases.</p>
          <Link to="/" className="btn-primary justify-center w-full">Back home</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen vector-grid pt-28 pb-24" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-5 mb-10">
          <div>
            <span className="eyebrow">Owner inbox</span>
            <h1 className="f-display text-3xl sm:text-4xl mt-3">Inquiries & purchases</h1>
          </div>
          <button onClick={load} disabled={busy} className="btn-ghost !py-2.5 !px-5" data-testid="admin-refresh-btn">
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 mb-10">
          <StatCard label="Inquiries" value={inquiries.length} testid="admin-stat-inquiries" />
          <StatCard label="Organizers sold" value={stats.paid_count} testid="admin-stat-sold" />
          <StatCard label="Revenue" value={`$${Number(stats.revenue).toLocaleString()}`} testid="admin-stat-revenue" />
        </div>

        <div className="flex gap-1 sm:gap-2 mb-8 p-1 rounded-full bg-[#FFFDF7] border border-[#E8DCCB] w-full sm:w-fit">
          {[["inquiries", Inbox, "Inquiries"], ["purchases", CreditCard, "Purchases"], ["clients", Users, "Clients"], ["pages", LayoutTemplate, "Pages"]].map(([id, Icon, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`flex flex-1 sm:flex-none items-center justify-center gap-2 rounded-full px-3 sm:px-5 py-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.12em] sm:tracking-[0.16em] transition-colors ${tab === id ? "bg-[#A8785A] text-[#FFFDF7]" : "text-[#8C7562] hover:text-[#3A2E25]"}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          {tab === "pages" ? (
            <AdminPages />
          ) : tab === "clients" ? (
            <AdminClients clients={clients} setClients={setClients} />
          ) : tab === "inquiries" ? (
            <div className="flex flex-col gap-4" data-testid="admin-inquiries-list">
              {inquiries.length === 0 && <p className="text-sm text-[#8C7562]">No inquiries yet — they'll appear here the moment someone submits the form.</p>}
              {inquiries.map((q, i) => <InquiryCard key={q.id || i} q={q} onReply={setReplyTo} />)}
            </div>
          ) : (
            <div className="glass rounded-2xl p-6" data-testid="admin-purchases-list">
              <div className="flex flex-wrap items-center gap-2 mb-5 pb-5 border-b border-[#E8DCCB]" data-testid="admin-email-previews">
                <span className="eyebrow !text-[10px] mr-2 w-full sm:w-auto">Preview client emails</span>
                {[["organizer", "Organizer receipt"], ["social_growth", "Social plan receipt"], ["website_signature", "Website receipt"]].map(([kind, label]) => (
                  <a key={kind} href={`${API_BASE}/admin/emails/preview?kind=${kind}&origin=${encodeURIComponent(window.location.origin)}&auth=${getToken()}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold border border-[#E8DCCB] bg-[#FFFDF7] rounded-full px-3.5 py-1.5 hover:border-[#A8785A] hover:text-[#A8785A] transition-colors"
                    data-testid={`admin-email-preview-${kind}`}>
                    <Eye size={12} /> {label}
                  </a>
                ))}
              </div>
              <div className="hidden sm:grid grid-cols-[1.2fr_1fr_0.7fr_0.8fr_1.2fr] gap-3 pb-3 border-b border-[#E8DCCB] eyebrow !text-[10px]">
                <span>Buyer</span><span>Started</span><span>Amount</span><span>Status</span><span>Receipt</span>
              </div>
              {purchases.length === 0 && <p className="text-sm text-[#8C7562] py-4">No checkouts yet.</p>}
              {purchases.map((p) => <PurchaseRow key={p.session_id} p={p} />)}
            </div>
          )}
        </motion.div>
      </div>
      <ReplyDrawer inquiry={replyTo} onClose={() => setReplyTo(null)} onSent={markReplied} />
    </main>
  );
}
